import { Link } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface InnerPageHeroProps {
  breadcrumbs: BreadcrumbItem[];
  title: string;
  titleAccent: string;
  titleSuffix?: string;
  description: string;
}

export default function InnerPageHero({
  breadcrumbs,
  title,
  titleAccent,
  titleSuffix,
  description,
}: InnerPageHeroProps) {
  return (
    <section className="relative bg-brand-navy overflow-hidden">
      <div className="absolute inset-0">
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hero-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="1" fill="white" />
              <path d="M0 0L40 40M40 0L0 40" stroke="white" strokeWidth="0.5" opacity="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-grid)" />
        </svg>
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-brand-orange/8 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingTop: '89px', paddingBottom: '89px' }}>
        <nav className="flex items-center gap-2 mb-5 sm:mb-6">
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && (
                <svg className="w-3.5 h-3.5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              )}
              {crumb.href ? (
                <Link to={crumb.href} className="text-white/50 text-sm hover:text-white/80 transition-colors">
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-brand-orange text-sm font-medium">{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>

        <div className="max-w-3xl">
          <h1 className="text-[32px] leading-[1.15] sm:text-[36px] md:text-4xl lg:text-[40px] xl:text-[44px] 2xl:text-[48px] font-serif font-bold text-white mb-3 sm:mb-4">
            {title}{' '}
            <span className="text-brand-orange">{titleAccent}</span>
            {titleSuffix && <>{' '}{titleSuffix}</>}
          </h1>

          <p className="text-base sm:text-[15px] lg:text-base text-white/70 leading-relaxed max-w-2xl">
            {description}
          </p>
        </div>
      </div>
    </section>
  );
}
